/* eslint-disable import/prefer-default-export */
/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React from 'react';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import Terminal from '../shared/entities/terminal';
import isNil from 'lodash/isNil';
import filter from 'lodash/filter';

const ONLINE = 'online'; 
const ACTIVE = 'active';
const STANDBY = 'standby';
export const OFFLINE = 'offline';

/**
 * This class represents the utility functions for connection status of aircraft.
 */
export const isOnline = (connectivityStatus: string | undefined): boolean => {
  return connectivityStatus === ONLINE || connectivityStatus === ACTIVE || connectivityStatus === STANDBY;
};

/**
 * Return true if the connectivity status represents an active state.
 * @param connectivityStatus the connectivity status to check if it is active
 */
export const isActive = (connectivityStatus: string | undefined): boolean => {
  return connectivityStatus === ONLINE || connectivityStatus === ACTIVE;
};

/**
 * Return true if the connectivity status represents an standby state.
 * @param connectivityStatus the connectivity status to check if it is standby
 */
export const isStandby = (connectivityStatus: string | undefined): boolean => {
  return connectivityStatus === STANDBY;
};

/**
 * Get the highest priority status for the list of statuses provided.
 * @param connStatuses the list of connection status strings to find the highest priority one from
 */
export const getHighestPriorityConnectivityStatus = (...connStatuses: (string | undefined)[]) => {
  if (isNil(connStatuses)) {
    return OFFLINE;
  }

  const validStatuses = filter(connStatuses, status => !isNil(status));
  if (validStatuses.length === 0) {
    return OFFLINE;
  }

  // at least one active status
  if (!isNil(validStatuses.find(status => isActive(status)))) {
    return ACTIVE;
  }
  // at least one standby status
  else if (!isNil(validStatuses.find(status => isStandby(status)))) {
    return STANDBY;
  } else {
    return OFFLINE;
  }
};

/**
 * Get the terminal in an aircraft that sent the most recent message.
 */
export const getMostRecentTerminalInAircraft = (
  terminal: Terminal,
  allTerminals: { [key: string]: Terminal },
): Terminal => {
  if (terminal) {
    const { datapathUuidsOnAircraft } = terminal;
    let mostRecentTerminal = terminal;

    if (datapathUuidsOnAircraft && datapathUuidsOnAircraft.length > 0) {
      datapathUuidsOnAircraft.forEach(otherDatapathUuid => {
        if (otherDatapathUuid) {
          const otherTerminal = allTerminals[otherDatapathUuid];

          if (otherTerminal && otherTerminal.sourceTime && otherTerminal.sourceTime > mostRecentTerminal.sourceTime) {
            mostRecentTerminal = otherTerminal;
          }
        }
      });
    }

    return mostRecentTerminal;
  }

  return terminal;
};

export const getConnectivityColor = (connectivityStatus: string | undefined) => {
  if (connectivityStatus === 'active' || connectivityStatus === 'online') {
    return '#5EA215';
  }
  if (connectivityStatus === 'standby') {
    return '#C5F042';
  }

  return '#C3CDD5';
};

export const getConnectivityIcon = (connectivityStatus: string) => {
  const commonStyle = {
    fontSize: '12px',
    width: '12px',
    height: '12px',
    color: getConnectivityColor(connectivityStatus),
  };

  if (connectivityStatus === 'active' || connectivityStatus === 'online') {
    return (
      <FiberManualRecordIcon
        data-test="connectivity-icon"
        style={{ ...commonStyle }}
        stroke={commonStyle.color}
        strokeWidth={4}
      />
    );
  }

  return (
    <FiberManualRecordIcon
      data-test="connectivity-icon"
      style={{ ...commonStyle }}
      fillOpacity={0}
      stroke={commonStyle.color}
      strokeWidth={4}
    />
  );
};

export const getConnectivityIntPriority = (connectivityStatus: string | undefined) => {
  if (isActive(connectivityStatus)) {
    return 2;
  } else if (isStandby(connectivityStatus)) {
    return 1;
  } else {
    return 0;
  }
};
